import React, { useState, useRef, useEffect, useMemo } from "react";
import { Chip, ChipIcon } from "@material-tailwind/react";
import { pascalToSentenceCase } from "../../utils/helpers";
import { X } from "lucide-react";

const AmenitiesSelect = ({ amenities, setAmenities }) => {
  const [inputValue, setInputValue] = useState("");

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const allAmenities = [
    "Club House",
    "Cycling & Jogging Track",
    "Power Back Up",
    "Lift",
    "Security",
    "Reserved Parking",
    "Service/Goods Lift",
    "Visitor Parking",
    "Maintenance Staff",
    "Laundry Service",
    "Rain Water Harvesting",
    "Recreational Pool",
    "Water Storage",
    "Multipurpose Courts",
    "Meditation Area ",
    "Activity Deck4 ",
    "Outdoor Tennis Courts",
    "Fire Fighting Equipment ",
  ];

  const selectedAmenities = useMemo(() => {
    if (!amenities) return [];
    return Array.isArray(amenities) ? amenities : amenities.split(',').filter(Boolean);
  }, [amenities]);

  const filteredAmenities = useMemo(() => {
    return allAmenities.filter(
      (amenity) =>
        amenity.toLowerCase().includes(inputValue.toLowerCase()) &&
        !(selectedAmenities?.includes(amenity))
    );
  }, [inputValue, selectedAmenities]);

  const handleSelect = (amenity) => {
    setAmenities([...selectedAmenities, amenity]);
    setInputValue("");
    setIsOpen(false);
  };

  const handleRemove = (amenityToRemove) => {
    setAmenities(
      selectedAmenities.filter((amenity) => amenity !== amenityToRemove)
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && inputValue.trim() !== "") {
      e.preventDefault();
      const newAmenity = inputValue.trim();
      if (!selectedAmenities?.includes(newAmenity)) {
        handleSelect(newAmenity);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="w-full">
      {/* Display selected amenities as chips */}
      {Array.isArray(selectedAmenities) && selectedAmenities.length > 0 ? (
        <div className="flex flex-wrap gap-2 ">
          {selectedAmenities.map((amenity) => (
            <Chip
              key={amenity}
              variant="outline"
              className="py-1 px-2 my-2 rounded-md text-sm"
              color="black"
            >
              {pascalToSentenceCase(amenity)}
              <ChipIcon
                className="ml-2 cursor-pointer"
                onClick={() => handleRemove(amenity)}
              >
                <X size={12} />
              </ChipIcon>
            </Chip>
          ))}
        </div>
      ) : (
        <div>No amenities selected.</div>
      )}

      {/* Input field with dropdown */}
      <div className="relative" ref={dropdownRef}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Add amenities..."
          className="w-full px-2 py-2 border border-slate-300 rounded-md focus:border-flamingo-500 focus:ring-0 focus:outline-none transition-colors duration-200 text-sm"
        />

        {/* Dropdown for filtered amenities */}
        {isOpen && filteredAmenities.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-auto">
            <ul className="py-1">
              {filteredAmenities?.map((amenity) => {
                return (
                  <li
                    key={amenity}
                    onClick={() => handleSelect(amenity)}
                    className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700 transition-colors"
                  >
                    {pascalToSentenceCase(amenity)}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default AmenitiesSelect;